// components/company/tabs/CompanyBankAccounts.jsx
import { useState, Fragment } from "react";
import { Field } from "../../ui/Field";
import { CountrySelect } from "../../ui/FormControls";
import { addCompanyBankAccount, updateCompanyBankAccount, deleteCompanyBankAccount } from "../../../api";
import { normalizeCountryId } from "../../../utils/helpers";

// Constants for magic strings (Fix #5)
const BANK_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
};

const CURRENCY_OPTIONS = [
  { value: "", label: "— Select currency —" },
  { value: "EUR", label: "EUR" },
  { value: "XCG", label: "XCG" },
  { value: "USD", label: "USD" },
];

export const CompanyBankAccounts = ({ bankAccounts, countries, countryById, companyId, notify, refetch }) => {
  const [editBankRow, setEditBankRow] = useState(null);
  const [newBank, setNewBank] = useState({
    bank_name: "",
    account_no: "",
    currency: "",
    country_id: "",
    opened_on: "",
    closed_on: "",
    status: BANK_STATUS.ACTIVE,
    note: "",
  });
  const [errors, setErrors] = useState({});

  // Enhanced loading state (Fix #2)
  const [loading, setLoading] = useState({
    add: false,
    edit: null,
    delete: null,
  });

  // Enhanced validation (Fix #3)
  const validateNewBank = () => {
    const newErrors = {};

    // Required fields
    if (!newBank.bank_name?.trim()) {
      newErrors.bank_name = "Bank name is required";
    }
    if (!newBank.account_no?.trim()) {
      newErrors.account_no = "Account number is required";
    }

    // Date logic validation
    if (newBank.opened_on && newBank.closed_on) {
      const opened = new Date(newBank.opened_on);
      const closed = new Date(newBank.closed_on);
      if (opened > closed) {
        newErrors.closed_on = "Closed date must be after opened date";
      }
    }

    // No future dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (newBank.opened_on) {
      const opened = new Date(newBank.opened_on);
      opened.setHours(0, 0, 0, 0);
      if (opened > today) {
        newErrors.opened_on = "Opened date cannot be in the future";
      }
    }

    return newErrors;
  };

  // Enhanced error handler (Fix #4)
  const handleError = (error, operation = "save") => {
    let message = `Failed to ${operation} bank account. Please try again.`;

    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data?.error || error.response.data?.message;

      if (status === 404) {
        message = errorData === "company_not_found" ? "Company not found" : "Bank account not found";
      } else if (status === 400) {
        message = errorData || "Invalid bank account data";
      } else if (status === 422) {
        message = errorData || "Invalid bank account data";
      } else if (status === 403) {
        message = "You don't have permission to modify bank accounts";
      } else if (status === 409) {
        message = "This bank account conflicts with existing data";
      } else if (errorData) {
        message = errorData;
      }
    } else if (error.message) {
      message = error.message;
    }

    notify(message);
    console.error(`[BankAccounts] ${operation} failed:`, error);
  };

  const handleAddBank = async (e) => {
    e.preventDefault();
    const newErrors = validateNewBank();
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setLoading((prev) => ({ ...prev, add: true }));
    try {
      await addCompanyBankAccount(companyId, {
        bank_name: newBank.bank_name,
        account_no: newBank.account_no,
        currency: newBank.currency || null,
        country_id: normalizeCountryId(newBank.country_id),
        opened_on: newBank.opened_on || null,
        closed_on: newBank.closed_on || null,
        status: newBank.status,
        note: newBank.note || null,
      });

      setNewBank({
        bank_name: "",
        account_no: "",
        currency: "",
        country_id: "",
        opened_on: "",
        closed_on: "",
        status: BANK_STATUS.ACTIVE,
        note: "",
      });
      setErrors({});
      await refetch();
      notify("Bank account added successfully");
    } catch (error) {
      handleError(error, "add");
    } finally {
      setLoading((prev) => ({ ...prev, add: false }));
    }
  };

  const startEditBankRow = (bankAccount) => {
    setEditBankRow({
      id: bankAccount.id,
      bank_name: bankAccount.bank_name || "",
      account_no: bankAccount.account_no || "",
      currency: bankAccount.currency || "",
      country_id: bankAccount.country_id ? String(bankAccount.country_id) : "",
      opened_on: bankAccount.opened_on || "",
      closed_on: bankAccount.closed_on || "",
      status: bankAccount.status || BANK_STATUS.ACTIVE,
      note: bankAccount.note || "",
    });
  };

  const saveBankEdit = async (e) => {
    e?.preventDefault?.();
    if (!editBankRow) return;

    setLoading((prev) => ({ ...prev, edit: editBankRow.id }));
    try {
      await updateCompanyBankAccount(companyId, editBankRow.id, {
        bank_name: editBankRow.bank_name,
        account_no: editBankRow.account_no,
        currency: editBankRow.currency || null,
        country_id: normalizeCountryId(editBankRow.country_id),
        opened_on: editBankRow.opened_on || null,
        closed_on: editBankRow.closed_on || null,
        status: editBankRow.status,
        note: editBankRow.note || null,
      });

      // Fix #1: Only close edit mode on SUCCESS
      setEditBankRow(null);
      await refetch();
      notify("Bank account updated successfully");
    } catch (error) {
      // Fix #1: Stay in edit mode on error so user can retry
      handleError(error, "update");
    } finally {
      setLoading((prev) => ({ ...prev, edit: null }));
    }
  };

  const removeBank = async (bankId) => {
    if (!confirm("Delete this bank account?")) return;

    setLoading((prev) => ({ ...prev, delete: bankId }));
    try {
      await deleteCompanyBankAccount(companyId, bankId);
      await refetch();
      notify("Bank account deleted successfully");
    } catch (error) {
      handleError(error, "delete");
    } finally {
      setLoading((prev) => ({ ...prev, delete: null }));
    }
  };

  // Helper to get country name from ID
  const getCountryName = (countryId) => {
    if (!countryId) return "—";

    // Try to get from countryById map
    const country = countryById.get(String(countryId));
    if (country) return country.name;

    // Fallback: try to find in countries array
    const countryObj = countries?.find((c) => String(c.id) === String(countryId));
    if (countryObj) return countryObj.name;

    // Last resort: show the ID
    return `Country #${countryId}`;
  };

  // Check if any operation is in progress
  const anyLoading = loading.add || loading.edit !== null || loading.delete !== null;

  return (
    <>
      <div className="card table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Bank</th>
              <th>Account</th>
              <th>Currency</th>
              <th>Country</th>
              <th>Opened</th>
              <th>Closed</th>
              <th>Status</th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bankAccounts.map((bankAccount) => {
              const editing = editBankRow && editBankRow.id === bankAccount.id;
              const isDeleting = loading.delete === bankAccount.id;

              return (
                <Fragment key={`ba-${bankAccount.id}`}>
                  <tr>
                    <td>{bankAccount.bank_name}</td>
                    <td>{bankAccount.account_no}</td>
                    <td>{bankAccount.currency || "—"}</td>
                    <td>{getCountryName(bankAccount.country_id)}</td>
                    <td>{bankAccount.opened_on?.slice(0, 10) || "—"}</td>
                    <td>{bankAccount.closed_on?.slice(0, 10) || "—"}</td>
                    <td>{bankAccount.status || "—"}</td>
                    <td className="cell-ellipsis">{bankAccount.note || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {!editing && (
                        <>
                          <button type="button" onClick={() => startEditBankRow(bankAccount)} disabled={anyLoading}>
                            Edit
                          </button>
                          <button type="button" className="secondary" onClick={() => removeBank(bankAccount.id)} style={{ marginLeft: 4 }} disabled={anyLoading || isDeleting}>
                            {isDeleting ? "Deleting..." : "Delete"}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>

                  {editing && (
                    <tr className="inline-editor-row">
                      <td colSpan={9}>
                        <form onSubmit={saveBankEdit}>
                          <div className="inline-editor">
                            <label>
                              <span>Bank</span>
                              <input value={editBankRow.bank_name} onChange={(e) => setEditBankRow({ ...editBankRow, bank_name: e.target.value })} />
                            </label>
                            <label>
                              <span>Account</span>
                              <input value={editBankRow.account_no} onChange={(e) => setEditBankRow({ ...editBankRow, account_no: e.target.value })} />
                            </label>
                            <label>
                              <span>Currency</span>
                              <select value={editBankRow.currency} onChange={(e) => setEditBankRow({ ...editBankRow, currency: e.target.value })}>
                                {CURRENCY_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              <span>Country</span>
                              <CountrySelect value={editBankRow.country_id} onChange={(val) => setEditBankRow({ ...editBankRow, country_id: val })} countries={countries} />
                            </label>
                            <label>
                              <span>Opened</span>
                              <input type="date" value={editBankRow.opened_on || ""} onChange={(e) => setEditBankRow({ ...editBankRow, opened_on: e.target.value })} />
                            </label>
                            <label>
                              <span>Closed</span>
                              <input type="date" value={editBankRow.closed_on || ""} onChange={(e) => setEditBankRow({ ...editBankRow, closed_on: e.target.value })} />
                            </label>
                            <label>
                              <span>Status</span>
                              <select value={editBankRow.status} onChange={(e) => setEditBankRow({ ...editBankRow, status: e.target.value })}>
                                <option value={BANK_STATUS.ACTIVE}>Active</option>
                                <option value={BANK_STATUS.INACTIVE}>Inactive</option>
                              </select>
                            </label>
                            <label className="span-2">
                              <span>Note</span>
                              <input value={editBankRow.note || ""} onChange={(e) => setEditBankRow({ ...editBankRow, note: e.target.value })} />
                            </label>
                          </div>

                          <div className="inline-editor__actions">
                            <button type="submit" disabled={loading.edit === editBankRow.id}>
                              {loading.edit === editBankRow.id ? "Saving..." : "Save"}
                            </button>
                            <button type="button" className="secondary" onClick={() => setEditBankRow(null)} disabled={loading.edit === editBankRow.id}>
                              Cancel
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {bankAccounts.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center">
                  No bank accounts
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <form className="card form-section" onSubmit={handleAddBank}>
        <h3>Add account</h3>
        <div className="form-grid">
          <Field label="Bank" required error={errors.bank_name}>
            <input value={newBank.bank_name} onChange={(e) => setNewBank({ ...newBank, bank_name: e.target.value })} required />
          </Field>

          <Field label="Account" required error={errors.account_no}>
            <input value={newBank.account_no} onChange={(e) => setNewBank({ ...newBank, account_no: e.target.value })} required />
          </Field>

          <Field label="Currency">
            <select value={newBank.currency} onChange={(e) => setNewBank({ ...newBank, currency: e.target.value })}>
              {CURRENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Country">
            <CountrySelect value={newBank.country_id} onChange={(val) => setNewBank({ ...newBank, country_id: val })} countries={countries} />
          </Field>

          <Field label="Opened" error={errors.opened_on}>
            <input type="date" value={newBank.opened_on} onChange={(e) => setNewBank({ ...newBank, opened_on: e.target.value })} />
          </Field>

          <Field label="Closed" error={errors.closed_on}>
            <input type="date" value={newBank.closed_on} onChange={(e) => setNewBank({ ...newBank, closed_on: e.target.value })} />
          </Field>

          <Field label="Status">
            <select value={newBank.status} onChange={(e) => setNewBank({ ...newBank, status: e.target.value })}>
              <option value={BANK_STATUS.ACTIVE}>Active</option>
              <option value={BANK_STATUS.INACTIVE}>Inactive</option>
            </select>
          </Field>

          <Field label="Note">
            <input value={newBank.note} onChange={(e) => setNewBank({ ...newBank, note: e.target.value })} />
          </Field>
        </div>
        <div className="form-actions">
          <button type="submit" disabled={anyLoading}>
            {loading.add ? "Adding..." : "Add account"}
          </button>
        </div>
      </form>
    </>
  );
};
